import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material';
import { AccountTree, CheckCircleOutline, HourglassEmpty, Cancel as CancelIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetInstancesByEntityQuery } from '../../../../../services/WorkflowService';
import { WorkflowInstanceView } from '../../../../../services/WorkflowService';
import { ApprovalRequest } from '../../../../../services/ApprovalService';
import { InlineApprovalAction } from './InlineApprovalAction';

const WorkflowApprovalsSection: React.FC = () => {
  const currentTicket = useAppSelector(state => state.ticket.currentTicket);
  const ticketId = currentTicket?.id;

  const { data: instances, isLoading, isError, refetch } = useGetInstancesByEntityQuery(
    ticketId || '',
    { skip: !ticketId }
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" action={<Button onClick={() => refetch()} size="small">Retry</Button>}>
        Failed to load workflow data.
      </Alert>
    );
  }

  if (!instances || instances.length === 0) {
    return (
      <Box py={3} textAlign="center">
        <AccountTree sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
        <Typography variant="body2" color="#64748b">
          No workflows have been triggered for this ticket.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {instances.map(instance => (
        <WorkflowInstanceCard key={instance.id} instance={instance} />
      ))}
    </Stack>
  );
};

// ── Instance Card ──────────────────────────────────────────────────

interface WorkflowInstanceCardProps {
  instance: WorkflowInstanceView;
}

const WorkflowInstanceCard: React.FC<WorkflowInstanceCardProps> = ({ instance }) => {
  const [expanded, setExpanded] = useState(true);
  const pendingApprovals = instance.approvalRequests.filter(a => a.isPending);
  const hasActionable = pendingApprovals.length > 0;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden' }}>
      {/* Instance header */}
      <Box
        sx={{
          px: 2, py: 1.5,
          backgroundColor: hasActionable ? '#eff6ff' : '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTree sx={{ fontSize: 18, color: '#3b82f6' }} />
            <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
              {instance.workflowName || 'Workflow'}
            </Typography>
            <InstanceStatusChip status={instance.status} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {hasActionable && (
              <Chip size="small" color="info" icon={<HourglassEmpty />} label={`${pendingApprovals.length} pending`} />
            )}
            {expanded ? <ExpandLess sx={{ color: '#64748b' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
          </Stack>
        </Stack>
        <Typography variant="caption" color="#64748b">
          Started {new Date(instance.startedAt).toLocaleString()} by {instance.triggeredBy}
        </Typography>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {/* Steps summary */}
          {instance.stepExecutions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={600} color="#64748b" sx={{ mb: 0.5, display: 'block' }}>
                Steps
              </Typography>
              <Stack spacing={0.5}>
                {instance.stepExecutions.map(step => (
                  <Stack key={step.stepId} direction="row" spacing={1} alignItems="center">
                    <StepStatusDot status={step.status} />
                    <Typography variant="body2" color="#1e293b">
                      {step.stepName || step.stepType}
                    </Typography>
                    <Typography variant="caption" color="#64748b">
                      ({step.status})
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* Approval requests */}
          {instance.approvalRequests.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={600} color="#64748b" sx={{ mb: 1, display: 'block' }}>
                Approval Requests
              </Typography>
              <Stack spacing={1.5}>
                {instance.approvalRequests.map(approval => (
                  <ApprovalRequestCard key={approval.id} approval={approval} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── Approval Request Card ──────────────────────────────────────────

interface ApprovalRequestCardProps {
  approval: ApprovalRequest;
}

const ApprovalRequestCard: React.FC<ApprovalRequestCardProps> = ({ approval }) => {
  return (
    <Paper elevation={0} sx={{ p: 1.5, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <ApprovalStatusIndicator approval={approval} />
            <Typography variant="body2" fontWeight={500} color="#1e293b">
              {approval.approverEmail}
            </Typography>
          </Stack>
          <Typography variant="caption" color="#64748b">
            Requested {new Date(approval.requestedAt).toLocaleString()}
            {approval.respondedAt && ` · Responded ${new Date(approval.respondedAt).toLocaleString()}`}
          </Typography>
          {approval.comment && (
            <Typography variant="caption" display="block" color="#475569" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              "{approval.comment}"
            </Typography>
          )}
        </Box>
      </Stack>

      {/* Inline approve/reject for pending approvals */}
      {approval.isPending && <InlineApprovalAction approval={approval} />}
    </Paper>
  );
};

// ── Helper components ──────────────────────────────────────────────

const InstanceStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'Completed' ? 'success'
    : status === 'Running' ? 'info'
    : status === 'Cancelled' || status === 'Failed' ? 'error'
    : 'default';
  return <Chip size="small" variant="outlined" color={color} label={status} />;
};

const StepStatusDot: React.FC<{ status: string }> = ({ status }) => {
  const color = status === 'Completed' ? '#22c55e'
    : status === 'Running' || status === 'WaitingForApproval' ? '#3b82f6'
    : status === 'Failed' ? '#ef4444'
    : '#94a3b8';
  return <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />;
};

const ApprovalStatusIndicator: React.FC<{ approval: ApprovalRequest }> = ({ approval }) => {
  if (approval.isApproved) return <CheckCircleOutline sx={{ fontSize: 16, color: '#22c55e' }} />;
  if (approval.isRejected) return <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} />;
  if (approval.isExpired) return <CancelIcon sx={{ fontSize: 16, color: '#f59e0b' }} />;
  return <HourglassEmpty sx={{ fontSize: 16, color: '#3b82f6' }} />;
};

export default WorkflowApprovalsSection;
